import { useState } from "react";
import { Tag, Loader2, X, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import api from "../lib/api";
import { usd } from "../lib/pricing";

// Promo codes live in the Premium checkout (never at signup — referral codes are a separate system).
// `lookupKey` is the plan being bought; the server decides whether the code applies to it.
export function PromoCodeBox({ offer, promo, setPromo, lookupKey = "pro_monthly" }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (offer.launch_active && lookupKey === "pro_monthly") {
    return <p className="mt-6 text-xs text-slate-400" data-testid="promo-unavailable-note"><Tag className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Promo codes can't be combined with the {usd(offer.monthly_cents)}/month launch price — you're already getting our best offer.</p>;
  }
  const apply = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/promos/validate", { code, lookup_key: lookupKey });
      if (data.valid) { setPromo(data); setOpen(false); } else { setErr(data.message || "That code isn't valid."); }
    } catch { setErr("Couldn't check that code. Try again."); }
    setBusy(false);
  };
  if (promo) {
    return (
      <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-100 flex items-start gap-2" data-testid="promo-applied">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
        <div className="flex-1"><p><span className="font-mono font-bold">{promo.code}</span> — {promo.description}</p><p className="text-xs text-emerald-200/80" data-testid="promo-applied-summary">{promo.summary}.</p></div>
        <button type="button" onClick={() => { setPromo(null); setCode(""); }} className="text-emerald-200 hover:text-white" aria-label="Remove promo code" data-testid="promo-remove"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="mt-6 text-xs text-slate-400 hover:text-white underline underline-offset-2" data-testid="promo-toggle">Have a promo code?</button>;
  return (
    <div className="mt-6" data-testid="promo-box">
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="PROMO CODE" className="h-10 bg-slate-900 border-slate-700 text-white font-mono tracking-widest uppercase" maxLength={24} data-testid="promo-input" onKeyDown={(e) => e.key === "Enter" && code && apply()} />
        <Button type="button" onClick={apply} disabled={busy || !code} variant="outline" className="h-10 border-slate-600 text-white hover:bg-slate-800" data-testid="promo-apply">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}</Button>
      </div>
      {err && <p className="mt-1.5 text-xs text-red-300" data-testid="promo-error">{err}</p>}
      <p className="mt-1.5 text-[11px] text-slate-500">Promo codes discount Premium subscriptions only (each code says whether it's for Monthly, Annual or both). One code per checkout.</p>
    </div>
  );
}
